// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';
import { getRange, getStart, getEnd } from '../utils/missingRefReportWindow';

// TYPES ---------------------------------------------------------------- //

export type Metric = 'all' | 'upcoming' | 'inprogress' | 'completed' | 'canceled';
export type SortOrder = 'asc' | 'desc';
export type ReportView = 'moves' | 'mobileservice';

interface MissingRefReportContextType {
  // State variables
  disablePickers: boolean;
  end: string;
  limit: number;
  metric: Metric;
  offset: number;
  order: SortOrder;
  orderBy: string;
  range: string;
  search: string;
  start: string;
  usernames: string[];
  view: ReportView;
  // Set when the host (a product tab) scopes the report rather than the user.
  lockedView?: ReportView;

  // State setters
  setDisablePickers: (disable: boolean) => void;
  setEnd: (end: string) => void;
  setLimit: (limit: number) => void;
  setMetric: (metric: Metric) => void;
  setOffset: (offset: number) => void;
  setOrder: (order: SortOrder) => void;
  setOrderBy: (orderBy: string) => void;
  setRange: (range: string) => void;
  setSearch: (search: string) => void;
  setStart: (start: string) => void;
  setUsernames: (users: string[]) => void;

  // Handlers
  onClearFilters: () => void;
  onDateChange: (type: 'start' | 'end') => (date: string) => void;
  onMetricChange: (metric: Metric) => void;
  onRangeChange: (range: string) => void;
  onViewChange: (view: ReportView) => void;
}

interface MissingRefReportProviderProps {
  children: React.ReactNode;
  /** Overrides the persisted preference. */
  defaultView?: ReportView;
  /** Pins the view and hides the internal switch. */
  lockedView?: ReportView;
}

// GETTERS ---------------------------------------------------------------- //

const getDisable = (range: string): boolean => {
  return range !== 'custom';
};

const getMetric = (): Metric => {
  try {
    return (localStorage.getItem(`missing-ref-report-metric`) as Metric) || 'all';
  } catch (err) {
    console.error(`Error getting metric`, err);
    return 'all';
  }
};

const getView = (): ReportView => {
  try {
    return localStorage.getItem(`missing-ref-report-view`) === 'mobileservice' ? 'mobileservice' : 'moves';
  } catch (err) {
    console.error(`Error getting view`, err);
    return 'moves';
  }
};

// CONTEXT ---------------------------------------------------------------- //

const MissingRefReportContext = React.createContext<MissingRefReportContextType | undefined>(undefined);

export const MissingRefReportProvider = ({ children, defaultView, lockedView }: MissingRefReportProviderProps) => {
  // STATE VARIABLES //

  // Filter State
  const [range, setRange] = React.useState<string>(getRange());
  const [start, setStart] = React.useState<string>(getStart(range));
  const [end, setEnd] = React.useState<string>(getEnd(range));
  const [disablePickers, setDisablePickers] = React.useState(getDisable(range));
  const [usernames, setUsernames] = React.useState<string[]>([]);
  const [search, setSearch] = React.useState<string>('');

  // Metric State
  const [metric, setMetric] = React.useState<Metric>(getMetric());

  // View State (moves vs mobile service stops)
  const [view, setView] = React.useState<ReportView>(lockedView ?? defaultView ?? getView());

  // Pagination State
  const [limit, setLimit] = React.useState<number>(50);
  const [offset, setOffset] = React.useState<number>(0);

  // Sorting State
  const [orderBy, setOrderBy] = React.useState<string>('ready_by');
  const [order, setOrder] = React.useState<SortOrder>('desc');

  // HANDLERS //

  /** Handle changing the range */
  const handleRangeChange = (newRange: string) => {
    try {
      localStorage.setItem('missing-ref-report-range', newRange);
      const newStart = getStart(newRange);
      const newEnd = getEnd(newRange);

      setRange(newRange);
      setStart(newStart);
      setEnd(newEnd);
      setDisablePickers(getDisable(newRange));
      setOffset(0);
    } catch (error) {
      console.error('Error changing range', error);
    }
  };

  /** Handle changing the date */
  const handleDateChange = (type: 'start' | 'end') => (date: string) => {
    if (!date) return;

    if (type === 'start') {
      const newStart = dayjs(date).startOf('day').format();
      const dateIsValid = new Date(newStart).getTime() > 0;
      if (!dateIsValid) return;

      localStorage.setItem('missing-ref-report-start', newStart);
      setStart(newStart);
      setOffset(0);
    } else {
      const newEnd = dayjs(date).endOf('day').format();
      const dateIsValid = new Date(newEnd).getTime() > 0;
      if (!dateIsValid) return;

      localStorage.setItem('missing-ref-report-end', newEnd);
      setEnd(newEnd);
      setOffset(0);
    }
  };

  /** Handle changing the metric */
  const handleMetricChange = (newMetric: Metric) => {
    localStorage.setItem('missing-ref-report-metric', newMetric);
    setMetric(newMetric);
    setOffset(0);
  };

  const handleViewChange = (newView: ReportView) => {
    if (lockedView) return;
    localStorage.setItem('missing-ref-report-view', newView);
    setView(newView);
    setOffset(0);
  };

  /** Handle clearing the filters */
  const handleClearFilters = () => {
    localStorage.removeItem('missing-ref-report-usernames');
    localStorage.removeItem('missing-ref-report-metric');

    setMetric('all');
    setSearch('');
    setUsernames([]);

    setOffset(0);
  };

  /** Returning context value */
  const contextValue: MissingRefReportContextType = {
    // State variables
    disablePickers,
    end,
    limit,
    metric,
    offset,
    order,
    orderBy,
    range,
    search,
    start,
    usernames,
    view,
    lockedView,

    // State setters
    setDisablePickers,
    setEnd,
    setLimit,
    setMetric,
    setOffset,
    setOrder,
    setOrderBy,
    setRange,
    setSearch,
    setStart,
    setUsernames,

    // Handlers
    onClearFilters: handleClearFilters,
    onDateChange: handleDateChange,
    onMetricChange: handleMetricChange,
    onRangeChange: handleRangeChange,
    onViewChange: handleViewChange,
  };

  return <MissingRefReportContext.Provider value={contextValue}>{children}</MissingRefReportContext.Provider>;
};

// HOOK ---------------------------------------------------------------- //

export const useMissingRefReport = (): MissingRefReportContextType => {
  const context = React.useContext(MissingRefReportContext);
  if (context === undefined) {
    throw new Error('useMissingRefReport must be used within a MissingRefReportProvider');
  }
  return context;
};
